// Wraps Firebase Storage operations. Previously spread across js/email.js (misnamed)
// and inline in several components. All functions use the storage singleton from
// config/firebase.js.
//
// Storage bucket layout:
//   singlePatterns/   — user-generated single-purchase PDFs
//   patternLibrary/   — admin-uploaded library pattern images

import {
	ref,
	uploadBytesResumable,
	getDownloadURL,
	updateMetadata,
	deleteObject,
	uploadString,
} from 'firebase/storage';
import { storage } from '../config/firebase';

// Generates a random 32-character alphanumeric filename using crypto.getRandomValues.
export const generateRandomFileName = () => {
	const nameLength = 32;
	const characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const randomValues = new Uint32Array(nameLength);
	window.crypto.getRandomValues(randomValues);
	let result = '';
	for (let i = 0; i < nameLength; i++) {
		result += characters.charAt(randomValues[i] % characters.length);
	}
	return result;
};

// Uploads a PDF blob to the given destination path in Storage.
// Returns a promise that resolves to the public download URL.
// customMetadata is stored on the file (e.g. { paid: "false", uid: "..." }).
export const uploadPatternPdf = async (
	pattern,
	destination,
	customMetadata,
) => {
	const fileName = `${generateRandomFileName()}.pdf`;
	const metadata = {
		contentType: 'application/pdf',
		customMetadata,
	};

	const fileRef = ref(storage, `${destination}/${fileName}`);
	const uploadTask = uploadBytesResumable(fileRef, pattern, metadata);

	return new Promise((resolve, reject) => {
		uploadTask.on(
			'state_changed',
			null,
			(error) => reject(error),
			() => {
				getDownloadURL(uploadTask.snapshot.ref)
					.then((downloadURL) => resolve(downloadURL))
					.catch((error) => reject(error));
			},
		);
	});
};

// Updates the custom metadata on an existing single-pattern PDF (e.g. marking it paid).
export const updatePatternMetadata = async (fileName, customMetadata) => {
	const patternRef = ref(storage, `singlePatterns/${fileName}.pdf`);
	return updateMetadata(patternRef, { customMetadata });
};

// Deletes a single-pattern PDF from Storage. Always calls callback regardless of success/failure.
export const deletePatternPdf = (fileName, callback) => {
	const patternRef = ref(storage, `singlePatterns/${fileName}.pdf`);
	return deleteObject(patternRef)
		.catch(() => {})
		.finally(() => callback());
};

// Uploads a library pattern image from a data URL to patternLibrary/ in Storage.
export const uploadLibraryImage = async (dataUrl, fileName) => {
	const fileRef = ref(storage, `patternLibrary/${fileName}`);
	return uploadString(fileRef, dataUrl, 'data_url');
};
